/**
 * A KPI row's "⋯" menu: Add To Dashboard, Edit, Duplicate, Refresh, Delete.
 * Delete asks first, as AgencyAnalytics does for KPIs. Add To Dashboard picks
 * one of the client's dashboards, appends the KPI there and opens it.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { go } from '../agency/routes.ts';
import { reportsApi, type Catalogue, type ReportDocument } from '../reports/reports-api.ts';
import { addKpiToDashboard } from './kpi-widget.ts';
import { kpisApi, type Kpi } from './kpis-api.ts';

type Dialog = 'delete' | 'dashboard' | null;

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function DashboardPicker(props: {
  organisationId: string;
  catalogue: Catalogue;
  kpi: Kpi;
  onClose: () => void;
  onProblem: (problem: string | null) => void;
}): ReactNode {
  const [dashboards, setDashboards] = useState<ReportDocument[] | null>(null);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const { organisationId, kpi, onProblem } = props;
  useEffect(() => {
    reportsApi
      .documents(organisationId, kpi.business_unit_id)
      .then((documents) => {
        const list = documents.filter((document) => document.kind === 'DASHBOARD');
        setDashboards(list);
        setChosen(list[0]?.id ?? '');
      })
      .catch((error: unknown) => onProblem(messageOf(error, 'Dashboards could not be loaded.')));
  }, [organisationId, kpi.business_unit_id, onProblem]);
  const add = async () => {
    setBusy(true);
    try {
      const added = await addKpiToDashboard(organisationId, chosen, kpi, props.catalogue);
      if (!added) throw new Error('That dashboard has no room for another widget.');
      go({ area: 'client', clientId: kpi.business_unit_id, tab: 'dashboards', documentId: chosen });
    } catch (error) {
      onProblem(messageOf(error, 'The KPI could not be added.'));
      props.onClose();
    }
  };
  return (
    <div className="rc kp-small" role="dialog" aria-modal="true" aria-labelledby="kp-add-title">
      <h2 id="kp-add-title">Add {kpi.title} to a dashboard</h2>
      {dashboards === null && <p>Loading dashboards…</p>}
      {dashboards?.length === 0 && (
        <p>{kpi.client_name} has no dashboards yet. Create one under Dashboards first.</p>
      )}
      {dashboards && dashboards.length > 0 && (
        <label className="rs-field">
          Dashboard
          <select value={chosen} onChange={(event) => setChosen(event.target.value)}>
            {dashboards.map((document) => (
              <option key={document.id} value={document.id}>
                {document.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="kp-dialog-actions">
        <button type="button" className="btn secondary" onClick={props.onClose}>
          Cancel
        </button>
        <button type="button" className="btn" disabled={!chosen || busy} onClick={() => void add()}>
          {busy ? 'Adding…' : 'Add To Dashboard'}
        </button>
      </div>
    </div>
  );
}

export function KpiRowMenu(props: {
  organisationId: string;
  catalogue: Catalogue;
  kpi: Kpi;
  onEdit: () => void;
  onChanged: (kpi: Kpi) => void;
  onRemoved: (id: string) => void;
  onProblem: (problem: string | null) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { organisationId, kpi } = props;
  useEffect(() => {
    if (!open && !dialog) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      setDialog(null);
    };
    const onPointer = (event: PointerEvent) =>
      open && box.current && !box.current.contains(event.target as Node) && setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open, dialog]);

  const act = (work: () => Promise<void>, fallback: string) => {
    setOpen(false);
    setBusy(true);
    props.onProblem(null);
    work()
      .catch((error: unknown) => props.onProblem(messageOf(error, fallback)))
      .finally(() => setBusy(false));
  };
  const choose = (next: () => void) => () => {
    setOpen(false);
    next();
  };
  const items: [string, () => void][] = [
    ['Add To Dashboard', choose(() => setDialog('dashboard'))],
    ['Edit', choose(props.onEdit)],
    [
      'Duplicate',
      () =>
        act(
          async () => props.onChanged(await kpisApi.duplicate(organisationId, kpi.id)),
          'The KPI could not be duplicated.',
        ),
    ],
    [
      'Refresh',
      () =>
        act(
          async () => props.onChanged(await kpisApi.refresh(organisationId, kpi.id)),
          'The KPI could not be refreshed.',
        ),
    ],
    ['Delete', choose(() => setDialog('delete'))],
  ];

  return (
    <div className="ex-filter" ref={box}>
      <button
        type="button"
        className="kp-more"
        aria-label={`Actions for ${kpi.title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen(!open)}
      >
        {busy ? '…' : '⋯'}
      </button>
      {open && (
        <ul className="ex-pop ex-pop-list kp-menu" role="menu">
          {items.map(([label, onClick]) => (
            <li role="none" key={label}>
              <button type="button" role="menuitem" onClick={onClick}>
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {dialog && (
        <div className="rc-scrim">
          {dialog === 'dashboard' ? (
            <DashboardPicker
              organisationId={organisationId}
              catalogue={props.catalogue}
              kpi={kpi}
              onClose={() => setDialog(null)}
              onProblem={props.onProblem}
            />
          ) : (
            <div
              className="rc kp-small"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="kp-del-title"
            >
              <h2 id="kp-del-title">Delete {kpi.title}?</h2>
              <p>
                The KPI, its target and its alert are removed. Dashboard widgets made from it stay.
              </p>
              <div className="kp-dialog-actions">
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setDialog(null)}
                  autoFocus
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn danger"
                  onClick={() => {
                    setDialog(null);
                    act(async () => {
                      await kpisApi.remove(organisationId, kpi.id);
                      props.onRemoved(kpi.id);
                    }, 'The KPI could not be deleted.');
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
